import { Text } from "@overmap-ai/blocks"
import { type RequiredFileModel, type Stored, type UUID } from "@overmap-ai/common"
import { BaseOfflineFileModelApiService } from "@overmap-ai/core"

import { AppSDK } from "../../sdk"
import type { RootState } from "../../store"
import { genericMemo } from "../../utils/optimization"
import { FileAttachmentCard } from "../Cards/FileAttachmentCard"
import { ImageAttachmentCard } from "../Cards/ImageAttachmentCard"
import { VideoAttachmentCard } from "../Cards/VideoAttachmentCard"

interface AttachmentListProps<TAttachment extends UUID<RequiredFileModel>> {
	attachments: Stored<TAttachment>[]
	onDelete?: (attachment: Stored<TAttachment>) => void
	service: BaseOfflineFileModelApiService<string, TAttachment, RootState, AppSDK>
}

export const AttachmentList = genericMemo(function <TAttachment extends UUID<RequiredFileModel>>(
	props: AttachmentListProps<TAttachment>,
) {
	const { attachments, onDelete, service } = props

	return attachments.length > 0 ? (
		<div className="flex w-full flex-col gap-2">
			{attachments.map((attachment) => {
				const { file_type } = attachment

				const isImageAttachment = !!(file_type && file_type.startsWith("image/"))
				const isVideoAttachment = !!(file_type && file_type.startsWith("video/"))

				if (isImageAttachment) {
					return (
						<ImageAttachmentCard<TAttachment>
							key={attachment.uuid}
							attachment={attachment}
							onDelete={onDelete}
							service={service}
						/>
					)
				}

				if (isVideoAttachment) {
					return (
						<VideoAttachmentCard<TAttachment>
							key={attachment.uuid}
							attachment={attachment}
							onDelete={onDelete}
							service={service}
						/>
					)
				}

				return (
					<FileAttachmentCard<TAttachment>
						key={attachment.uuid}
						attachment={attachment}
						onDelete={onDelete}
						service={service}
					/>
				)
			})}
		</div>
	) : (
		<Text accentColor="base" size="sm" weight="regular">
			No attachments.
		</Text>
	)
})
